import { FilterModel, GridApi, IServerSideGetRowsParams, RowHeightParams } from '@ag-grid-community/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import moment from 'moment';
import { Injector } from '@angular/core';
import { LuceneExpression, toString as luceneToString } from 'lucene';

import { ShipmentsService } from '../../../../order/services/shipments.service';
import { DatacenterSelectionService } from '../../../services/datacenter-selection.service';
import { Shipment } from '../../../../order/models/shipment';
import { OrderBy, OrderByDirection } from '../../../utils/constant';
import { PaginatedResult } from '../../../models/paginated-result';
import { formatTextDimension } from '../../../utils/number.format';
import { FilterDateFormatType, DATE_FORMAT_PATTERN_DD_MM_YYYY,
	WEEKYEAR_FORMAT_PATTERN } from '../../../models/filter-formats';
import { CommonGridOptions } from '../../grid-options';
import { ColDefWithFields, ColumnType } from '../../column-type';
import { Strategy } from '../strategy';
import { DatacenterType, UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { FilterModel as FilterModelValue, FilterType } from '../../../models/ag-grid';
import { DatacenterFilterUtilsService } from '../../../services/datacenter-filter-utils.service';
import { createDateFilter, createDimensionFilter, createDropDownFilter, createNumberFilter, createReferenceItemFilter,
	createShipmentDimensionFilter,
	createShipmentMultiValueDateFilter,
	createShipmentMultiValueDropDownFilter,
	createShipmentMultiValueNumberFilter,
	createShipmentMultiValueTextFilter,
	createTextFilter } from '../../../services/datacenter-filter';

export class StrategyShipments implements Strategy<Shipment> {
	shipmentsService: ShipmentsService;
	selectionService: DatacenterSelectionService;
	defaultOrderBy = new OrderBy<keyof Shipment>('dispatchAdviceNumber', OrderByDirection.DESC);
	hasTotal = true;
	aggregateFields = ['qtyShipmentNet', 'qtyShipmentBrut'];
	filterService: DatacenterFilterUtilsService;

	constructor(injector: Injector) {
		this.shipmentsService = injector.get<ShipmentsService>(ShipmentsService);
		this.selectionService = injector.get<DatacenterSelectionService>(DatacenterSelectionService);
		this.filterService = injector.get<DatacenterFilterUtilsService>(DatacenterFilterUtilsService);
	}

	setTotal(api?: GridApi, filter?: string) {
		return this.shipmentsService.getAggregatedQuantityForShipments(filter)
			.subscribe({
				next: value => {
					api?.setGridOption('pinnedTopRowData', [value]);
				},
				error: (err: unknown) => {
					console.error('error loading', err);
				}
			});
	}

	async getAllDataSelected(
		currentColumns: string[],
		filter: string | undefined,
		orderBy: OrderBy<keyof Shipment>[],
	) {
		return this.getAllData(orderBy, currentColumns, filter)
			.then((shipments: PaginatedResult<Shipment>) => this.removeDeselectedRows(shipments));
	}

	async getAllData(
		orderBy: OrderBy<keyof Shipment>[],
		currentColumns: string[],
		filter: string | undefined
	) {
		return firstValueFrom(this.shipmentsService.getDatacenterShipments(
			<(keyof Shipment)[]> currentColumns,
			filter,
			0,
			60_000,
			orderBy,
		));
	}

	formatCellByType(columnDef: ColDefWithFields<Shipment>, data: Shipment) {
		const field = columnDef.field || '';
		const colId = columnDef.colId || '';
		const fields = columnDef.fields || [];
		const { columnType } = columnDef;
		switch (columnType) {
		case ColumnType.DIMENSION_COLUMN:
			return formatTextDimension(
				<string> data[<keyof Shipment> fields[0]],
				<string> data[<keyof Shipment> fields[1]],
				<string> data[<keyof Shipment> fields[2]]
			);
		case ColumnType.ORDER_NUMBER_COLUMN:
		{
			let orderNumber = '';
			if (<string> data[<keyof Shipment> fields[0]]) {
				orderNumber = <string> data[<keyof Shipment> fields[0]];
			}
			if (<string> data[<keyof Shipment> fields[0]] && <string> data[<keyof Shipment> fields[1]]) {
				orderNumber += `/${ <string> data[<keyof Shipment> fields[1]] }`;
			}
			return orderNumber;
		}
		case ColumnType.NUMBER_COLUMN:
		{
			const value = <number | undefined >data[<keyof Shipment> field];
			return value === undefined ? '' : String(value);
		}
		case ColumnType.TEXT_COLUMN:
			if (typeof columnDef.valueGetter === 'function') {
				const valueGetter = <(params: unknown) => unknown><unknown>columnDef.valueGetter;
				const val = valueGetter({ data });
				return typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean'
					? String(val)
					: '';
			}
			return columnDef.fields?.some(colDef => colDef === 'batchReferenceConcat')
				? <string> data[<keyof Shipment> fields[0]]
				: colId === 'dimension' ? <string> data[<keyof Shipment> colId] : <string> data[<keyof Shipment> field];
		case ColumnType.TRANSLATED_COLUMN:
			if (field === 'supplierOrderTypeConcat') {
				return <string> data[<keyof Shipment> field];
			}
			if (field === 'status') {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
				return <string> data[<keyof Shipment> columnDef.filterParams?.batchFields[1]];
			}
			// Prefer valueGetter when available (covers columns that compute/display descriptions)
			if (typeof columnDef.valueGetter === 'function') {
				const valueGetter = <(params: unknown) => unknown><unknown>columnDef.valueGetter;
				const val = valueGetter({ data });
				return String(val);
			}
			{
				// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
				const shipmentFieldIndex = columnDef.filterParams?.shipmentFields ? 1 : undefined;
				return <string> (shipmentFieldIndex !== undefined
					// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
					? data[<keyof Shipment> columnDef.filterParams.shipmentFields[shipmentFieldIndex]]
					: data[<keyof Shipment> field]);
			}
		case ColumnType.DATE_COLUMN:
			if (data[<keyof Shipment> field]) {
				const dateformat = (<FilterDateFormatType> columnDef.cellRendererParams).outputFormat === 'week'
					? WEEKYEAR_FORMAT_PATTERN : DATE_FORMAT_PATTERN_DD_MM_YYYY;
				if ((<string> data[<keyof Shipment> field]).includes(';')) {
					const weekArray = (<string> data[<keyof Shipment> field]).split(';');
					return weekArray.map(value => moment.utc(value).format(dateformat)).join(';');
				}
				const weekData = dateformat === WEEKYEAR_FORMAT_PATTERN && data[<keyof Shipment> field] === '1900-W01'
					? '' : data[<keyof Shipment> field];
				return weekData === '' ? '' : moment.utc(<string> weekData).format(dateformat);
			}
			return '';
		default:
			throw new Error('unknown filter type');
		}
	}

	getCustomGridOptions(defaultGridOptions: CommonGridOptions) {
		return new CommonGridOptions<Shipment>({
			...defaultGridOptions,
			getRowId: ({ data }: { data: Shipment }) => data.supplierShipmentNumber,
			getRowHeight(params: RowHeightParams) {
				if (!params.data) {
					return undefined;
				}
				const data = <Shipment> params.data;
				if (!data.batchReferenceConcat) {
					return undefined;
				}
				const matches = data.batchReferenceConcat.match(/;/g);
				const count = matches ? matches.length : 0;
				return ((count + 1) * 30) + 2;
			},
		});
	}

	getDataFromGrid(
		orderBy: OrderBy<keyof Shipment>[],
		currentColumns: (keyof Shipment)[],
		filter: string | undefined,
		params: IServerSideGetRowsParams<Shipment>,
		rowsCountSubject: BehaviorSubject<number | undefined>
	) {
		const currentColumnsWithId = this.addIdColumn(currentColumns);
		return this.shipmentsService.getDatacenterShipments(
			currentColumnsWithId,
			filter,
			params.request.startRow ?? 0,
			250,
			orderBy
		).subscribe({
			next: res => {
				const lastRow = params.request.endRow === undefined || res.count <= params.request.endRow
					? res.count
					: -1;
				params.success({ rowData: res.results, rowCount: lastRow });
				rowsCountSubject.next(res.count);
			},
			error: (err: unknown) => {
				console.error('error requesting shipment datacenter', err);
				rowsCountSubject.next(undefined);
				params.fail();
			}
		});
	}

	addIdColumn(currentColumns: (keyof Shipment)[]) {
		if (!currentColumns.includes('supplierShipmentNumber')) {
			currentColumns.push('supplierShipmentNumber');
		}
		if (!currentColumns.includes('form')) {
			currentColumns.push('form');
		}
		return currentColumns;
	}

	setDataIntoGrid(params: IServerSideGetRowsParams, res: PaginatedResult<Shipment>) {
		const lastRow = params.request.endRow === undefined || res.count <= params.request.endRow ? res.count : -1;
		params.success({ rowData: res.results, rowCount: lastRow });
	}

	createAggregateCustomViewFilter(view: UserDatacenterView) {
		if (this.filterService.getLuceneFromAllFilters(view)) {
			const criterionFilterNode = this.filterService.getLuceneFromAllFilters(view);
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			return luceneToString(criterionFilterNode!);
		}
		return '';
	}

	createAggregateQuickSearchFilter(
		currentSelectedView: UserDatacenterView,
		api?: GridApi,
		filterModel?: FilterModel
	) {
		let criterionFilterNode;
		let filter = '';
		if (!filterModel) {
			return filter;
		}
		for (const [columnId, filterModel1] of Object.entries(<Record<string, FilterModelValue>> filterModel)) {
			const field = <DatacenterType | undefined> api?.getColumnDef(columnId)?.field;
			const colId = <DatacenterType | undefined> api?.getColumnDef(columnId)?.colId;
			const { fields } = <ColDefWithFields> api?.getColumnDef(columnId);
			const { multiFilterCodes } = <ColDefWithFields> api?.getColumnDef(columnId);
			let criterionFilterValueNode;
			switch (filterModel1.customFilterName) {
			case FilterType.DIMENSION_FILTER:
				criterionFilterValueNode = createDimensionFilter(filterModel1);
				break;
			case FilterType.SHIPMENT_DIMENSION_FILTER:
				criterionFilterValueNode = createShipmentDimensionFilter(filterModel1);
				break;
			case FilterType.ORDER_NUMBER_FILTER:
				criterionFilterValueNode = createReferenceItemFilter(filterModel1, fields);
				break;
			case FilterType.NUMBER_FILTER:
				criterionFilterValueNode = createNumberFilter(filterModel1, field);
				break;
			case FilterType.SHIPMENT_MULTI_VALUE_NUMBER_FILTER:
				criterionFilterValueNode = createShipmentMultiValueNumberFilter(filterModel1, field);
				break;
			case FilterType.DATE_FILTER:
				criterionFilterValueNode = createDateFilter(filterModel1.date, field);
				break;
			case FilterType.SHIPMENT_MULTI_VALUE_DATE_FILTER:
				criterionFilterValueNode = createShipmentMultiValueDateFilter(
					filterModel1.date,
					field
				);
				break;
			case FilterType.DROPDOWN_FILTER:
				criterionFilterValueNode = createDropDownFilter(
					filterModel1.text,
					colId,
					multiFilterCodes
				);
				break;
			case FilterType.SHIPMENT_MULTI_VALUE_DROPDOWN_FILTER:
				criterionFilterValueNode = createShipmentMultiValueDropDownFilter(
					filterModel1.text,
					colId,
					multiFilterCodes
				);
				break;
			case FilterType.TEXT_FILTER:
				criterionFilterValueNode = createTextFilter(filterModel1, field);
				break;
			case FilterType.SHIPMENT_MULTI_VALUE_TEXT_FILTER:
				criterionFilterValueNode = createShipmentMultiValueTextFilter(filterModel1, field);
				break;
			default:
				throw new Error('unknown filter type');
			}
			if (!criterionFilterNode) {
				criterionFilterNode = criterionFilterValueNode;
				continue;
			}
			criterionFilterNode = {
				left: criterionFilterNode,
				operator: 'AND',
				right: criterionFilterValueNode,
				parenthesized: false
			};
		}
		if (this.filterService.getLuceneFromAllFilters(currentSelectedView)) {
			criterionFilterNode = !criterionFilterNode
				? this.filterService.getLuceneFromAllFilters(currentSelectedView)
				: {
					left: criterionFilterNode,
					operator: 'AND',
					right: this.filterService.getLuceneFromAllFilters(currentSelectedView),
					parenthesized: true,
				};
		}
		filter = luceneToString(<LuceneExpression> criterionFilterNode);
		return filter;
	}

	private removeDeselectedRows(batches: PaginatedResult<Shipment>) {
		return batches.results.filter(shipment => {
			const rowDeselected = this.selectionService.findDeselectedNode(shipment.supplierShipmentNumber);
			return !rowDeselected;
		});
	}
}
